import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { isBefore, isAfter, addDays, subDays, isEqual } from "date-fns";

import useData from "../../hooks/useData";
import useLocalization from "../../hooks/useLocalization";
import useSurvey from "../../hooks/useSurvey";
import usePage from "../../hooks/usePage";
import useClient from "../../hooks/useClient";
import useLocation from "../../hooks/useLocation";

import { Autocomplete, Box, Stack, TextField, Typography } from "@mui/material";

import EditorToolbar from "../editors/EditorToolbar";

import InfoBox from "../layout/InfoBox";
import SurveyEntry_Page from "./SurveyEntry_Page";
import Modal from "../modals/Modal";

// Modes
// ===========================
// - test
// - entry
// - view

const SurveyEntry = ({
  survey,
  mode,
  callId,
  onChange,
  onSave,
  onRefresh,
  isDisabled,
}) => {
  const sameIdSequence = (left = [], right = []) => {
    if (left.length !== right.length) return false;

    for (let i = 0; i < left.length; i++) {
      if ((left[i]?.id || null) !== (right[i]?.id || null)) {
        return false;
      }
    }

    return true;
  };

  const { getFromServer, sortByFields, executeAction, updateRecord, newId } =
    useData();
  const { getLabel, getProvinceName, getMessage, getTitle } = useLocalization();
  const { getSurveyStatus, showPage } = useSurvey(survey);
  const { showMessage } = usePage();
  const { getClient } = useClient();

  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [call, setCall] = useState(null);
  const [callResult, setCallResult] = useState(null);
  const [resolvedSurveyType, setResolvedSurveyType] = useState("survey");
  const [saveErrors, setSaveErrors] = useState([]);

  const [pages, setPages] = useState(null);
  const [surveyPages, setSurveyPages] = useState([]);
  const [sections, setSections] = useState(null);
  const [questions, setQuestions] = useState(null);

  const [currentPage, setCurrentPage] = useState(null);

  const [answers, setAnswers] = useState([]);

  const [userId, setUserId] = useState(null);
  const [callDate, setCallDate] = useState(null);
  const [location, setLocation] = useState(null);
  const [startedAt, setStartedAt] = useState(new Date());
  const [finishedAt, setFinishedAt] = useState(null);
  const [callDuration, setCallDuration] = useState(null);
  const [mileage, setMileage] = useState(null);
  const [firstCall, setFirstCall] = useState(false);
  const [lastCall, setLastCall] = useState(false);

  const [users, setUsers] = useState(null);
  const [locations, setLocations] = useState(null);

  const currentUserId = useSelector((state) => state.authentication.userId);

  const [openModal, setOpenModal] = useState(false);
  const [hasCallInfo, setHasCallInfo] = useState(false);
  const autoSaveQueueRef = useRef(Promise.resolve());

  const shouldDebugEntryFlow =
    typeof survey?.name === "string" &&
    survey.name.includes("WD-40 WM Corrugate");

  const summarizeAnswer = (answer) => {
    if (!answer) return null;

    return {
      id: answer.id || null,
      surveyQuestionId: answer.surveyQuestionId || null,
      surveySectionId: answer.surveySectionId || null,
      surveyPageId: answer.surveyPageId || null,
      questionType: answer.questionType || null,
      matrixItemId: answer.matrixItemId || null,
      skuId: answer.skuId || null,
      hasValue: answer.value !== null && answer.value !== undefined,
      valueType: Array.isArray(answer.value) ? "array" : typeof answer.value,
    };
  };

  const debugEntryFlow = (label, payload = {}) => {
    if (!shouldDebugEntryFlow) return;

    console.log("[SurveyEntryDebug]", label, {
      surveyId: survey?.id || null,
      surveyName: survey?.name || null,
      callId: call?.id || callId || null,
      mode,
      ...payload,
    });
  };

  useEffect(() => {
    getData();
  }, [survey, callId]);

  const getVisiblePages = async ({
    surveyPages,
    surveyAnswers,
    surveyLocation,
  }) => {
    if (!Array.isArray(surveyPages)) return [];

    let activePages = [];
    for (const page of surveyPages) {
      const show = await showPage({
        page,
        answers: surveyAnswers || [],
        location: surveyLocation || null,
      });
      if (!show) continue;
      activePages.push(page);
    }

    return sortByFields(activePages, ["pageNumber"]);
  };

  const getData = async () => {
    if (!survey) return;

    try {
      const projectData = await getFromServer({
        entityName: "Project",
        id: survey.projectId,
      });
      setProject(projectData);

      const clientData = await getClient(projectData.clientId);
      setClient(clientData);

      const surveyFull = await getFromServer({
        entityName: "Survey",
        pipeline: "full",
        id: survey.id,
      });

      const surveyTypeValue =
        surveyFull?.surveyType || survey?.surveyType || "survey";
      setResolvedSurveyType(surveyTypeValue);

      if (Array.isArray(surveyFull.sections)) {
        setSections(sortByFields(surveyFull.sections, ["sectionNumber"]));
      } else {
        setSections([]);
      }

      if (Array.isArray(surveyFull.questions)) {
        setQuestions(sortByFields(surveyFull.questions, ["questionNumber"]));
      } else {
        setQuestions([]);
      }

      if (Array.isArray(surveyFull.pages)) {
        setSurveyPages(sortByFields(surveyFull.pages, ["pageNumber"]));
      } else {
        setSurveyPages([]);
      }

      let effectiveLocation = null;
      let effectiveAnswers = [];

      if (callId) {
        const callData = await getFromServer({
          entityName: "Call",
          id: callId,
          pipeline: "full",
        });

        console.log("callData: ", callData);

        setCall(callData);
        setCallDate(callData.scheduleDate);
        setFinishedAt(callData.finishedAt ? callData.finishedAt : null);
        setStartedAt(callData.startedAt ? callData.startedAt : new Date());
        setCallDuration(callData.callDuration ? callData.callDuration : null);
        setFirstCall(callData.firstCall !== null ? callData.firstCall : false);
        setLastCall(callData.lastCall !== null ? callData.lastCall : false);
        setMileage(callData.mileage ? callData.mileage : null);

        effectiveLocation = callData.location || null;
        setLocation(effectiveLocation);

        const callResultData = await getFromServer({
          entityName: "CallResult",
          pipeline: "full",
          filter: { callId, surveyId: survey.id },
        });

        const currentCallResult =
          callResultData.length > 0 ? callResultData[0] : null;

        if (currentCallResult) {
          setCallResult({
            ...currentCallResult,
            surveyType:
              currentCallResult.surveyType || surveyTypeValue || "survey",
            status: mode === "entry" ? "inprogress" : currentCallResult.status,
            startedAt: currentCallResult.startedAt
              ? currentCallResult.startedAt
              : new Date(),
          });

          const surveyAnswers = await getFromServer({
            entityName: "SurveyAnswer",
            filter: {
              callResultId: currentCallResult.id,
            },
          });

          if (Array.isArray(surveyAnswers)) {
            effectiveAnswers = surveyAnswers;
            setAnswers(surveyAnswers);
          } else {
            setAnswers([]);
          }
        } else {
          setCallResult({
            localId: newId(),
            id: null,
            callId,
            surveyId: survey.id,
            surveyType: surveyTypeValue || "survey",
            status: "inprogress",
            startedAt: new Date(),
          });
          setAnswers([]);
        }
      }

      if (mode === "test") {
        try {
          const userData = await getFromServer({
            entityName: "User",
            filter: {
              isActive: true,
            },
            options: {
              sort: { name: 1 },
            },
          });
          setUsers(Array.isArray(userData) ? userData : []);
        } catch (err) {
          console.error("Error fetching users for test mode:", err);
          setUsers([]);
        }

        setLocations([]);

        setCall({
          clientId: clientData.id,
          client: { id: clientData.id },
          projectId: survey.projectId,
          locationId: null,
          scheduleYear: new Date().getFullYear(),
          scheduleMonth: new Date().getMonth(),
          scheduleDate: new Date(),
          userId: null,
          firstCall: false,
          lastCall: false,
          mileage: null,
          completedOn: new Date(),
          completedBy: null,
          startedAt: new Date(),
          finishedAt: null,
          callDuration: null,
          locked: false,
          longitude: null,
          latitude: null,
        });

        setCallResult({
          localId: newId(),
          _id: null,
          callId: null,
          surveyId: null,
          surveyType: surveyTypeValue || "survey",
          surveyStatus: "testing",
        });
      }
    } catch (err) {
      console.error("SurveyEntry.getData error:", {
        surveyId: survey?.id,
        callId,
        message: err?.message,
      });
      setPages([]);
      setSurveyPages([]);
      setSections([]);
      setQuestions([]);
      setCurrentPage(null);
      showMessage("Unable to load survey entry", "error");
    }
  };

  useEffect(() => {
    let cancelled = false;

    const rebuildVisiblePages = async () => {
      if (!Array.isArray(surveyPages) || surveyPages.length === 0) {
        if (!cancelled) {
          setPages([]);
          setCurrentPage(null);
        }
        return;
      }

      const nextVisiblePages = await getVisiblePages({
        surveyPages,
        surveyAnswers: answers,
        surveyLocation: location,
      });

      if (cancelled) return;

      setPages((prev) =>
        sameIdSequence(Array.isArray(prev) ? prev : [], nextVisiblePages)
          ? prev
          : nextVisiblePages,
      );

      setCurrentPage((prev) => {
        const stillVisible = prev
          ? nextVisiblePages.find((page) => page.id === prev.id)
          : null;

        return stillVisible || nextVisiblePages[0] || null;
      });
    };

    rebuildVisiblePages();

    return () => {
      cancelled = true;
    };
  }, [surveyPages, answers, location]);

  useEffect(() => {
    if (call && call.scheduleDate && call.locationId && call.userId) {
      setHasCallInfo(true);
    } else {
      setHasCallInfo(false);
    }
  }, [call]);

  // useEffect(() => {
  //   if (!currentPage) return;

  //   if (!showPage(currentPage)) {
  //     handleNextPage();
  //   }
  // }, [currentPage]);

  const getLocationsForRep = async (userId) => {
    try {
      let data = {
        userId,
      };

      if (project.syndicationId) {
        data.syndicationId = { $ne: null };
      } else {
        data.projectId = survey.projectId;
      }

      const results = await executeAction({
        entityName: "Location",
        action: "getForSurveyEntry",
        data: {
          match: data,
        },
      });

      const locationData =
        results?.isSuccessful && Array.isArray(results.data)
          ? results.data
          : [];

      if (locationData.length) {
        setLocations(
          locationData.map((location) => {
            return {
              id: location.id,
              name: location.name,
              streetAddress: location.address.streetAddress,
              city: location.address.city,
              province: getProvinceName(location.address.province),
              provinceCode: location.address.province,
              bannerId: location.bannerId,
              retailerId: location.retailerId,
            };
          }),
        );
      } else {
        setLocations([]);
      }
    } catch (err) {
      console.error("Error fetching locations for rep:", err);
      setLocations([]);
    }
  };

  const handleCallChange = (field, newValue) => {
    switch (field) {
      case "callDate":
        const newDate = newValue ? newValue : new Date();

        setCallDate(newDate);

        setCall((current) => {
          return { ...current, scheduleDate: newDate };
        });

        break;

      case "location":
        setLocation(newValue);

        setCall((current) => {
          return {
            ...current,
            locationId: newValue ? newValue.id : null,
            location: newValue
              ? { ...newValue, address: { province: newValue.provinceCode } }
              : null,
            banner: newValue
              ? { id: newValue.bannerId, retailerId: newValue.retailerId }
              : null,
          };
        });

        break;

      case "userId":
        setUserId(newValue);
        setLocation(null);

        let userId = null;
        if (newValue && newValue.id) userId = newValue.id;

        if (userId) getLocationsForRep(userId);

        setCall((current) => {
          return { ...current, userId: userId };
        });

        break;
    }
  };

  const handleAnswer = (newAnswer) => {
    if (isDisabled) return;
    onChange();

    const parseJsonSafe = (value) => {
      if (typeof value !== "string") return value;
      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    };

    const normalizeControlledBy = (controlledBy) => {
      const parsed = parseJsonSafe(controlledBy);
      if (!parsed || typeof parsed !== "object") return null;
      if (!parsed.questionId) return null;
      return parsed;
    };

    const normalizeToArray = (value) => {
      if (Array.isArray(value)) return value;
      return [value];
    };

    const normalizeComparable = (value) => {
      if (value === null || value === undefined || value === "") return null;
      return String(value);
    };

    const extractControlledValue = (answerValue, controlledBy) => {
      const parsedAnswerValue = parseJsonSafe(answerValue);

      if (
        controlledBy?.field &&
        parsedAnswerValue &&
        typeof parsedAnswerValue === "object" &&
        !Array.isArray(parsedAnswerValue)
      ) {
        return parsedAnswerValue[controlledBy.field];
      }

      return parsedAnswerValue;
    };

    const controlledByMatches = (controlledBy, answerValue) => {
      const allowedValues = normalizeToArray(controlledBy?.value)
        .map((item) => normalizeComparable(item))
        .filter(Boolean);

      if (allowedValues.length === 0) {
        return false;
      }

      const valueToCheck = extractControlledValue(answerValue, controlledBy);

      if (Array.isArray(valueToCheck)) {
        const normalizedValues = valueToCheck
          .map((item) => normalizeComparable(item))
          .filter(Boolean);

        return normalizedValues.some((item) => allowedValues.includes(item));
      }

      const normalized = normalizeComparable(valueToCheck);
      if (!normalized) return false;

      return allowedValues.includes(normalized);
    };

    const sameScope = (leftAnswer, rightAnswer) => {
      const leftSkuId = leftAnswer?.skuId || null;
      const rightSkuId = rightAnswer?.skuId || null;
      if (leftSkuId !== rightSkuId) return false;

      const leftMatrixItemId = leftAnswer?.matrixItemId || null;
      const rightMatrixItemId = rightAnswer?.matrixItemId || null;
      if (leftMatrixItemId !== rightMatrixItemId) return false;

      return true;
    };

    const pruneDependentAnswers = (collection) => {
      if (!Array.isArray(collection) || !Array.isArray(questions)) {
        return collection || [];
      }

      const questionById = new Map(
        questions.map((question) => [question.id, question]),
      );

      let nextAnswers = [...collection];
      let changed = true;

      while (changed) {
        changed = false;

        for (let i = 0; i < nextAnswers.length; i++) {
          const candidate = nextAnswers[i];
          const candidateQuestion = questionById.get(
            candidate.surveyQuestionId,
          );
          const controlledBy = normalizeControlledBy(
            candidateQuestion?.controlledBy,
          );

          if (!controlledBy) continue;

          const controllingAnswer = nextAnswers.find(
            (answer) =>
              answer.surveyQuestionId === controlledBy.questionId &&
              sameScope(answer, candidate),
          );

          const keepAnswer = controllingAnswer
            ? controlledByMatches(controlledBy, controllingAnswer.value)
            : false;

          if (!keepAnswer) {
            nextAnswers.splice(i, 1);
            changed = true;
            break;
          }
        }
      }

      return nextAnswers;
    };

    let answersForPersist = [];
    let removedAnswers = [];

    setAnswers((current) => {
      let newAnswers = [];
      let updated = false;

      if (Array.isArray(current) && current.length > 0) {
        for (const answer of current) {
          if (newAnswer.matrixItemId) {
            if (
              answer.matrixItemId === newAnswer.matrixItemId &&
              answer.surveyQuestionId === newAnswer.surveyQuestionId
            ) {
              newAnswers.push(newAnswer);
              updated = true;
              continue;
            }
            newAnswers.push(answer);
          } else if (newAnswer.skuId) {
            if (
              answer.skuId === newAnswer.skuId &&
              answer.surveyQuestionId === newAnswer.surveyQuestionId
            ) {
              newAnswers.push(newAnswer);
              updated = true;
              continue;
            }
            newAnswers.push(answer);
          } else {
            if (answer.surveyQuestionId === newAnswer.surveyQuestionId) {
              newAnswers.push(newAnswer);
              updated = true;
              continue;
            }
            newAnswers.push(answer);
          }
        }
      }

      if (!updated) {
        newAnswers.push(newAnswer);
      }

      const prunedAnswers = pruneDependentAnswers(newAnswers);
      answersForPersist = prunedAnswers;

      const nextAnswerIds = new Set(
        prunedAnswers
          .map((answer) => answer.id)
          .filter((id) => id !== null && id !== undefined && id !== ""),
      );

      removedAnswers = newAnswers.filter((answer) => {
        const id = answer?.id;
        if (!id) return false;
        return !nextAnswerIds.has(id);
      });

      return prunedAnswers;
    });

    if (mode === "entry" && Array.isArray(answersForPersist)) {
      enqueueAutoSave(async () => {
        try {
          debugEntryFlow("persistAnswerChanges.start", {
            changedAnswer: summarizeAnswer(newAnswer),
            answersForPersistCount: answersForPersist.length,
            removedAnswersCount: removedAnswers.length,
            removedAnswerIds: removedAnswers.map((item) => item?.id || null),
          });

          await persistAnswerChanges(
            newAnswer,
            answersForPersist,
            removedAnswers,
          );

          debugEntryFlow("persistAnswerChanges.success", {
            changedAnswer: summarizeAnswer(newAnswer),
          });
        } catch (err) {
          console.error("SurveyEntry.handleAnswer auto-save error:", {
            message: err?.message,
            surveyQuestionId: newAnswer?.surveyQuestionId,
            skuId: newAnswer?.skuId,
            matrixItemId: newAnswer?.matrixItemId,
          });
          debugEntryFlow("persistAnswerChanges.error", {
            changedAnswer: summarizeAnswer(newAnswer),
            error: err?.message || "Unknown auto-save error",
          });
        }
      });
    }
  };

  // const showPage = (page) => {
  //   const isAvailable = availability_passed(page);
  //   if (!isAvailable) return false;

  //   const isVisible = visibility_passed(page);
  //   if (!isVisible) return false;

  //   return logic_passed(page);
  // };

  // const availability_passed = (page) => {
  //   if (!page.availability) return true;
  //   if (page.availability.length === 0) return true;

  //   let isAvailable = false;

  //   for (const availability of page.availability) {
  //     if (availability.startsOn === null) {
  //       if (isBefore(call.completedOn, addDays(availability.endsOn, 1))) {
  //         isAvailable = true;
  //         break;
  //       }
  //     }
  //     if (availability.endsOn === null) {
  //       if (isAfter(call.completedOn, subDays(availability.endsOn, 1))) {
  //         isAvailable = true;
  //         break;
  //       }
  //     }
  //     if (
  //       isAfter(call.completedOn, subDays(availability.startsOn, 1)) &&
  //       isBefore(call.completedOn, addDays(availability.endsOn, 1))
  //     ) {
  //       isAvailable = true;
  //       break;
  //     }
  //   }

  //   return isAvailable;
  // };

  // const visibility_passed = (page) => {
  //   if (!page.visibility) return true;
  //   if (!location) return true;

  //   for (const visibility of page.visibility) {
  //     if (visibility.name === "province") {
  //       if (visibility.value !== location.provinceCode) {
  //         return false;
  //       }
  //     } else if (visibility.name === "retailer") {
  //       if (visibility.value !== location.retailerId) {
  //         return false;
  //       }
  //     } else if (visibility.name === "banner") {
  //       if (visibility.value !== location.bannerId) {
  //         return false;
  //       }
  //     }
  //   }

  //   return true;
  // };

  // const logic_passed = (page) => {
  //   if (page.controlledBy === null) return true;
  //   if (!page.controlledBy.questionId) return true;

  //   const controlAnswer = answers.find(
  //     (answer) => answer.surveyQuestionId === page.controlledBy.questionId
  //   );

  //   if (controlAnswer) {
  //     if (Array.isArray(page.controlledBy.value)) {
  //       if (Array.isArray(controlAnswer.value)) {
  //         for (const value of controlAnswer.value) {
  //           if (page.controlledBy.value.includes(value)) {
  //             return true;
  //           }
  //         }
  //       } else {
  //         if (page.controlledBy.value.includes(controlAnswer.value)) {
  //           return true;
  //         }
  //       }
  //     }
  //   }

  //   const sectionAnswers = answers.filter(
  //     (answer) => answer.surveyPageId === page.id
  //   );

  //   for (const answer of sectionAnswers) {
  //     if (answer.value) {
  //       handleAnswer({ ...answer, value: null });
  //     }
  //   }

  //   return false;
  // };

  const handleTestSave = () => {
    // const surveyStatus = getSurveyStatus(pages, sections, questions, answers);
    const surveyStatus = getSurveyStatus({
      call,
      surveyId: survey.id,
      answers,
    });

    if (!surveyStatus.completed) {
      showMessage(getMessage("missingAnswers"), "error");
      return;
    }

    showMessage(getMessage("passedValidation"), "success");
  };

  const sanitizeSurveyAnswerForSave = (answer, callResultData) => {
    const {
      qaComments,
      imageApprovals,
      passedQA,
      qaUserId,
      qaReviewedAt,
      ...safeAnswer
    } = answer;

    return {
      ...safeAnswer,
      callResultId: callResultData.id,
      callResultLocalId: callResultData.localId || callResult?.localId,
    };
  };

  const formatFieldError = (error) => {
    if (!error || typeof error !== "object") return null;

    const name = String(error.name || "").trim();
    const message = String(error.message || "").trim();

    if (name && message) return `${name}: ${message}`;
    if (name) return name;
    if (message) return message;
    return null;
  };

  const extractResponseErrorMessages = (results) => {
    const messages = [];

    if (Array.isArray(results?.fieldErrors)) {
      for (const fieldError of results.fieldErrors) {
        const formatted = formatFieldError(fieldError);
        if (formatted) messages.push(formatted);
      }
    }

    const topLevelMessage =
      typeof results?.message === "string" ? results.message.trim() : "";

    if (messages.length === 0 && topLevelMessage) {
      messages.push(topLevelMessage);
    }

    if (messages.length === 0) {
      messages.push("Unable to save answer");
    }

    return messages;
  };

  const enqueueAutoSave = (work) => {
    autoSaveQueueRef.current = autoSaveQueueRef.current
      .then(() => work())
      .catch((err) => {
        console.error("SurveyEntry auto-save queue error:", err);
      });
  };

  const getExistingCallResult = async () => {
    if (!call?.id || !survey?.id) return null;

    const callResultRows = await getFromServer({
      entityName: "CallResult",
      filter: { callId: call.id, surveyId: survey.id },
    });

    if (!Array.isArray(callResultRows) || callResultRows.length === 0) {
      return null;
    }

    return callResultRows[0] || null;
  };

  const ensureCallResultIdentity = async (payload) => {
    if (payload?.id) {
      return payload;
    }

    const existing = await getExistingCallResult();
    if (!existing?.id) {
      return payload;
    }

    const nextPayload = {
      ...payload,
      id: existing.id,
      localId: payload?.localId || existing.localId,
      surveyType:
        payload?.surveyType ||
        existing.surveyType ||
        resolvedSurveyType ||
        survey?.surveyType ||
        "survey",
    };

    setCallResult((current) => ({
      ...existing,
      ...(current || {}),
      ...nextPayload,
    }));

    return nextPayload;
  };

  const persistCallResultStatus = async (nextAnswers) => {
    if (mode !== "entry" || !call?.id || !survey?.id) {
      return { persistedCallResult: null };
    }

    debugEntryFlow("persistCallResultStatus.start", {
      nextAnswersCount: Array.isArray(nextAnswers) ? nextAnswers.length : null,
      callResultId: callResult?.id || null,
      callResultLocalId: callResult?.localId || null,
    });

    const surveyStatus = await getSurveyStatus({
      call,
      surveyId: survey.id,
      answers: nextAnswers,
    });

    const currentCallResult = callResult || {
      localId: newId(),
      id: null,
      callId: call.id,
      surveyId: survey.id,
      surveyType: resolvedSurveyType || survey?.surveyType || "survey",
      status: "inprogress",
      startedAt: new Date(),
    };

    const resultPayload = {
      ...currentCallResult,
      callId: call.id,
      surveyId: survey.id,
      surveyType:
        currentCallResult.surveyType ||
        resolvedSurveyType ||
        survey?.surveyType ||
        "survey",
      status: "inprogress",
      surveyStatus: surveyStatus.completed ? "complete" : "incomplete",
      startedAt: currentCallResult.startedAt || new Date(),
    };

    const callResultPayload = await ensureCallResultIdentity(resultPayload);

    const updatedCallResult = await updateRecord(
      "CallResult",
      callResultPayload,
      false,
    );

    debugEntryFlow("persistCallResultStatus.response", {
      isSuccessful: updatedCallResult?.isSuccessful === true,
      message: updatedCallResult?.message || null,
      hasData: !!updatedCallResult?.data,
      payloadId: callResultPayload?.id || null,
      payloadLocalId: callResultPayload?.localId || null,
      responseId: updatedCallResult?.data?.id || null,
      responseLocalId: updatedCallResult?.data?.localId || null,
    });

    if (!updatedCallResult?.isSuccessful || !updatedCallResult?.data) {
      throw new Error(
        updatedCallResult?.message || "Failed to update CallResult",
      );
    }

    setCallResult(updatedCallResult.data);

    return {
      persistedCallResult: updatedCallResult.data,
    };
  };

  const persistAnswerChanges = async (
    changedAnswer,
    nextAnswers,
    removedAnswers,
  ) => {
    if (mode !== "entry" || isDisabled || !call?.id || !survey?.id) {
      return;
    }

    const { persistedCallResult } = await persistCallResultStatus(nextAnswers);
    if (!persistedCallResult?.id) {
      debugEntryFlow("persistAnswerChanges.noCallResultId", {
        changedAnswer: summarizeAnswer(changedAnswer),
      });
      return;
    }

    for (const removed of removedAnswers) {
      if (!removed?.id) continue;

      const deleteResults = await executeAction({
        entityName: "SurveyAnswer",
        action: "delete",
        id: removed.id,
      });

      debugEntryFlow("persistAnswerChanges.deleteResponse", {
        removedAnswerId: removed.id,
        isSuccessful: deleteResults?.isSuccessful === true,
        message: deleteResults?.message || null,
      });

      if (!deleteResults?.isSuccessful) {
        const messages = extractResponseErrorMessages(deleteResults);
        throw new Error(messages.join("; "));
      }
    }

    const matchesAnswerIdentity = (left, right) => {
      if (!left || !right) return false;

      if (
        left.matrixItemId ||
        right.matrixItemId ||
        left.skuId ||
        right.skuId
      ) {
        return (
          (left.surveyQuestionId || null) ===
            (right.surveyQuestionId || null) &&
          (left.matrixItemId || null) === (right.matrixItemId || null) &&
          (left.skuId || null) === (right.skuId || null)
        );
      }

      return (
        (left.surveyQuestionId || null) === (right.surveyQuestionId || null)
      );
    };

    const changedAnswerStillVisible = Array.isArray(nextAnswers)
      ? nextAnswers.some((answerItem) =>
          matchesAnswerIdentity(answerItem, changedAnswer),
        )
      : false;

    if (!changedAnswerStillVisible) {
      debugEntryFlow("persistAnswerChanges.skipUpdatePrunedAnswer", {
        changedAnswer: summarizeAnswer(changedAnswer),
      });
      return;
    }

    const newSurveyAnswer = sanitizeSurveyAnswerForSave(
      changedAnswer,
      persistedCallResult,
    );

    const updatedSurveyAnswer = await updateRecord(
      "SurveyAnswer",
      newSurveyAnswer,
      false,
    );

    debugEntryFlow("persistAnswerChanges.updateResponse", {
      changedAnswer: summarizeAnswer(changedAnswer),
      payloadAnswer: summarizeAnswer(newSurveyAnswer),
      isSuccessful: updatedSurveyAnswer?.isSuccessful === true,
      message: updatedSurveyAnswer?.message || null,
      fieldErrors: updatedSurveyAnswer?.fieldErrors || [],
      responseAnswer: summarizeAnswer(updatedSurveyAnswer?.data),
    });

    if (!updatedSurveyAnswer?.isSuccessful) {
      const messages = extractResponseErrorMessages(updatedSurveyAnswer);
      throw new Error(messages.join("; "));
    }

    if (updatedSurveyAnswer?.data?.id) {
      setAnswers((current) => {
        const index = current.findIndex((answer) => {
          if (updatedSurveyAnswer.data.matrixItemId) {
            return (
              answer.matrixItemId === updatedSurveyAnswer.data.matrixItemId &&
              answer.surveyQuestionId ===
                updatedSurveyAnswer.data.surveyQuestionId
            );
          }

          if (updatedSurveyAnswer.data.skuId) {
            return (
              answer.skuId === updatedSurveyAnswer.data.skuId &&
              answer.surveyQuestionId ===
                updatedSurveyAnswer.data.surveyQuestionId
            );
          }

          return (
            answer.surveyQuestionId ===
            updatedSurveyAnswer.data.surveyQuestionId
          );
        });

        if (index === -1) return current;

        const next = [...current];
        next[index] = updatedSurveyAnswer.data;
        return next;
      });
    }
  };

  const handleSave = async () => {
    if (isDisabled) return;

    // const surveyStatus = getSurveyStatus(pages, sections, questions, answers);
    const surveyStatus = await getSurveyStatus({
      call,
      surveyId: survey.id,
      answers,
    });

    let newCallData = {
      id: call.id,
      syndicationId: call.syndicationId ? call.syndicationId : null,
      clientId: call.clientId,
      projectId: survey.projectId,
      locationId: call.locationId,
      userId: call.userId,
      scheduleYear: call.scheduleYear,
      scheduleMonth: call.scheduleMonth,
      scheduleDate: call.scheduleDate,
      startedAt: startedAt,
      finishedAt: finishedAt ? finishedAt : new Date(),
      completedBy: call.userId,
      completedOn: call.completedOn ? call.completedOn : new Date(),
      firstCall: firstCall,
      lastCall: lastCall,
      callDuration: callDuration,
      mileage: mileage,
    };

    const updatedCall = await updateRecord("Call", newCallData, false);

    if (!updatedCall.isSuccessful) {
      showMessage(updatedCall.message, "error");
      return;
    }

    setCall(updatedCall.data);

    let newCallResult = {
      ...callResult,
      callId: updatedCall.data.id,
      surveyId: survey.id,
      surveyType:
        callResult?.surveyType ||
        resolvedSurveyType ||
        survey?.surveyType ||
        "survey",
      surveyStatus: surveyStatus.completed ? "complete" : "incomplete",
      finishedAt: callResult?.finishedAt ? callResult.finishedAt : new Date(),
    };

    newCallResult = await ensureCallResultIdentity(newCallResult);

    const updatedCallResult = await updateRecord(
      "CallResult",
      newCallResult,
      false,
    );

    if (!updatedCallResult.isSuccessful) {
      showMessage(updatedCallResult.message, "error");
      return;
    }
    const callResultData = updatedCallResult.data;

    setCallResult(callResultData);

    const nextSaveErrors = [];

    for (const answer of answers) {
      const newSurveyAnswer = sanitizeSurveyAnswerForSave(
        answer,
        callResultData,
      );

      const updatedSurveyAnswer = await updateRecord(
        "SurveyAnswer",
        newSurveyAnswer,
        false,
      );

      if (!updatedSurveyAnswer.isSuccessful) {
        const question = questions.find(
          (q) => q.id === answer.surveyQuestionId,
        );
        const messages = extractResponseErrorMessages(updatedSurveyAnswer);
        nextSaveErrors.push({
          question: { ...question },
          message: messages.join("\n"),
        });
      }
    }

    setSaveErrors(nextSaveErrors);

    if (nextSaveErrors.length > 0) {
      setOpenModal(true);
    } else {
      if (surveyStatus.completed) {
        showMessage("Survey Complete");
      }

      onSave(
        surveyStatus.completed ? "complete" : "incomplete",
        updatedCall.data,
      );
    }
  };

  const handleNextPage = () => {
    setCurrentPage((current) => {
      if (!current || !Array.isArray(pages) || pages.length === 0)
        return current;

      const currentIndex = pages.findIndex((p) => p.id === current.id);
      if (currentIndex >= 0 && currentIndex < pages.length - 1) {
        return pages[currentIndex + 1];
      }

      return current;
    });
  };

  const handlePrevPage = () => {
    setCurrentPage((current) => {
      if (!current || !Array.isArray(pages) || pages.length === 0)
        return current;

      const currentIndex = pages.findIndex((p) => p.id === current.id);
      if (currentIndex > 0) {
        return pages[currentIndex - 1];
      }

      return current;
    });
  };

  const allowPrev = () => {
    if (!currentPage || !Array.isArray(pages) || pages.length === 0)
      return false;

    const currentIndex = pages.findIndex((p) => p.id === currentPage.id);
    return currentIndex > 0;
  };

  const allowNext = () => {
    if (!currentPage || !Array.isArray(pages) || pages.length === 0)
      return false;

    const currentIndex = pages.findIndex((p) => p.id === currentPage.id);
    return currentIndex >= 0 && currentIndex < pages.length - 1;
  };

  if (!call) return;

  return (
    <>
      <EditorToolbar
        onSave={null}
        message={mode === "test" ? "TEST MODE" : null}
        customOptions={
          mode === "test"
            ? [
                {
                  name: "testSave",
                  iconName: "Quiz",
                  label: "Test Save",
                  size: "small",
                },
              ]
            : []
        }
        onCustomOption={mode === "test" ? handleTestSave : null}
      />
      <Box padding="10px">
        <InfoBox
          label={getLabel("callInfo")}
          sx={{ backgroundColor: "#eee" }}
        >
          {mode === "test" &&
          Array.isArray(locations) &&
          Array.isArray(users) ? (
            <Stack gap={1}>
              <TextField
                size="small"
                type="date"
                value={callDate}
                onChange={(e) => handleCallChange("callDate", e.target.value)}
              />

              <Autocomplete
                size="small"
                options={users}
                getOptionKey={(option) => option.id}
                getOptionLabel={(option) => {
                  let label = `${option.name} ${option.territory}`;
                  return label;
                }}
                renderOption={(props, option) => {
                  return (
                    <>
                      <Stack
                        {...props}
                        style={{ marginBottom: "2px" }}
                      >
                        <div style={{ fontSize: "12px", fontWeight: "bold" }}>
                          {option.name}
                        </div>
                        <div
                          style={{ fontSize: "10px" }}
                        >{`${option.territory}`}</div>
                      </Stack>
                    </>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Rep"
                  />
                )}
                value={userId}
                onChange={(e, newValue) => handleCallChange("userId", newValue)}
              ></Autocomplete>

              <Autocomplete
                size="small"
                options={locations}
                getOptionKey={(option) => option.id}
                getOptionLabel={(option) => {
                  let label = `${option.name} ${option.streetAddress} ${option.city} ${option.province}`;
                  return label;
                }}
                renderOption={(props, option) => {
                  return (
                    <>
                      <Stack
                        {...props}
                        style={{ marginBottom: "2px" }}
                      >
                        <Box style={{ fontSize: "12px", fontWeight: "bold" }}>
                          {option.name}
                        </Box>
                        <Box
                          style={{ fontSize: "10px" }}
                        >{`${option.streetAddress}`}</Box>
                        <Box style={{ fontSize: "10px" }}>
                          {`${option.city}, ${option.province}`}
                        </Box>
                      </Stack>
                    </>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Location"
                  />
                )}
                value={location}
                onChange={(e, newValue) =>
                  handleCallChange("location", newValue)
                }
              ></Autocomplete>
            </Stack>
          ) : null}

          {mode === "entry" && call ? (
            <Stack>
              <Stack sx={{ marginBottom: "10px" }}>
                <Typography sx={{ fontSize: "10pt", fontWeight: "bold" }}>
                  {location?.name}
                </Typography>
                <Typography sx={{ fontSize: "8pt" }}>
                  {location?.address?.streetAddress}
                </Typography>
                <Typography sx={{ fontSize: "8pt" }}>
                  {`${location?.address?.city}, ${location?.address?.province}, ${location?.address?.postal}`}
                </Typography>
              </Stack>
            </Stack>
          ) : null}
        </InfoBox>
      </Box>

      {currentPage !== null && hasCallInfo ? (
        <Box
          sx={{
            paddingLeft: "8px",
            paddingRight: "8px",
            paddingBottom: "10px",
          }}
        >
          <SurveyEntry_Page
            callResultId={callResult?.id}
            page={currentPage}
            sections={sections}
            questions={questions}
            answers={answers}
            call={call}
            location={location}
            onChange={handleAnswer}
            onNextPage={handleNextPage}
            onPrevPage={handlePrevPage}
            allowPrev={allowPrev}
            allowNext={allowNext}
            isDisabled={isDisabled}
            testMode={mode === "test"}
          />
        </Box>
      ) : null}

      {!hasCallInfo && (
        <Box sx={{ padding: "10px" }}>
          <Typography>
            Complete the Call Information to active the survey
          </Typography>
        </Box>
      )}

      {openModal && saveErrors.length > 0 ? (
        <Modal
          title={getTitle("surveyErrors")}
          open={openModal}
          onClose={() => setOpenModal(false)}
        >
          <Stack spacing={1}>
            {saveErrors.map((err) => (
              <Box>
                <Typography sx={{ fontWeight: "bold", fontSize: "10px" }}>
                  {err.question.name}
                </Typography>
                <Typography sx={{ fontSize: "8px" }}>{err.message}</Typography>
              </Box>
            ))}
          </Stack>
        </Modal>
      ) : null}
    </>
  );
};

export default SurveyEntry;
